import axios, { AxiosResponse } from 'axios';
import { notification } from 'antd';
import auth from 'src/services/auth';
import { defaultErrorMessage } from 'src/constants/errorMessages';

type ResponseData = {
  response?: {
    responseHeader?: ResponseHeader;
  };
};

type ResponseHeader = {
  statusCode?: string;
  statusMessage?: string;
};

/**
 * [ UPDATE ] Sometimes API responds with XML or plain text
 * It's required because API responds with status: 200 even for 400, 401, ... status codes.
 * Such structure of response was accepted a long while ago due to old versions of Java
 * and some difficulties with the correct sending of proper HTTP status code.
 */
function validateResponse({ data }: AxiosResponse<ResponseData | string | null>) {
  // handling the case when API responds with a plain text or XML
  if (typeof data === 'string') {
    if (data.trim().startsWith('<?xml')) {
      throw new Error(data || defaultErrorMessage);
    }
    // any string that is not started with `<?xml` is considered as a successful message
    return;
  }

  const header = data?.response?.responseHeader;
  const status = Number(header?.statusCode) || 200;
  if (status < 200 || status > 299) {
    throw new Error(header?.statusMessage || defaultErrorMessage);
  }
}

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

instance.defaults.headers.get['Content-Type'] = 'application/json';

instance.interceptors.request.use((request) => {
  const userId = auth.getUserId();
  const token = auth.getToken();
  if (userId && token) {
    request.headers['userId'] = auth.getUserId();
    request.headers['token'] = auth.getToken();
  } else {
    auth.logout();
  }
  return request;
});

instance.interceptors.response.use(
  (response) => {
    /**
     * Special handling of unsuccessful API response
     * since API responds with status: 200 even for 400, 401, ... status codes
     */
    try {
      validateResponse(response);
    } catch (err) {
      return Promise.reject(err);
    }
    /** End of Special handling */

    const data = response.data.response || response.data;

    if (response.status === 206) {
      return response;
    }
    if (response.status === 200) {
      return response;
    }
    if (response.status === 202) {
      return response;
    }
    if (data && data.errorCode) {
      if (data.errorCode.toString() === '401') {
        auth.logout();
        return Promise.reject(defaultErrorMessage);
      } else {
        notification.error({
          message: 'An error occurred.',
          description: data.errorMessage || defaultErrorMessage,
          duration: 0,
        });
        return Promise.reject(data.errorMessage);
      }
    }
    return data;
  },
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (error.response && error.response.status && error.response.status === 401) {
      auth.logout();
    } else if (!error.response || (error.response && error.response.status && error.response.status === 500)) {
      //show common notification
      notification.error({
        message: 'Server not responding.',
        description: defaultErrorMessage,
        duration: 0,
      });
    }
    return Promise.reject(error);
  }
);

export default instance;
